import {ChangeDetectionStrategy, Component, computed, HostBinding, inject, OnInit, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {Project} from 'src/app/core/model/project';
import {TitleService} from 'src/app/core/page/title.service';
import {GetProjectsSummaryAction} from 'src/app/core/store/projects/projects.action';
import {selectProjectsSummary} from 'src/app/core/store/projects/projects.selector';
import {ProjectsDialogComponent} from 'src/app/projects/dialogs/projects-dialog/projects-dialog.component';
import {OphNewButtonComponent} from 'src/app/shared/design/oph-new-button/oph-new-button.component';
import {OphLoadingModule} from 'src/app/shared/design/oph-loading/oph-loading.module';
import {
  CsSalvageDialogComponent,
  CsSalvageDialogTask,
} from '../challenge/cs-salvage-dialog/cs-salvage-dialog.component';
import {PROJECTS_DIALOG_CONFIG} from 'src/app/projects/shared/projects-constants';
import {MobileService} from 'src/app/core/page/mobile.service';

@Component({
  selector: 'current-sked-initial-selection',
  standalone: true,
  imports: [MatIconModule, OphNewButtonComponent, OphLoadingModule],
  templateUrl: './current-sked-initial-selection.component.html',
  styleUrl: './current-sked-initial-selection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrentSkedInitialSelectionComponent implements OnInit {
  private readonly mobileService = inject(MobileService);
  @HostBinding('class') hostClasses = 'oph-feature-layout oph-feature-padding';

  private store$ = inject(Store);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private titleService = inject(TitleService);

  projectsSummary = toSignal(this.store$.select(selectProjectsSummary), {initialValue: null as Partial<Project>[]});
  loading = signal(false);
  challengesSummary = computed(() => this.projectsSummary()?.filter(p => p?.isChallenge) ?? []);
  projectsSummaryFiltered = computed(() => this.projectsSummary()?.filter(p => !p?.isChallenge) ?? []);
  showLoading = computed(() => this.loading() && this.projectsSummary() === null);

  constructor() {}

  ngOnInit() {
    this.titleService.setPageTitle('Current Sked');

    this.loading.set(true);
    this.store$.dispatch(
      new GetProjectsSummaryAction({
        onSuccess: () => this.loading.set(false),
        onFailure: () => this.loading.set(false),
      })
    );
  }

  onBack() {
    window.history.back();
  }

  onMySkedClick() {
    this.router.navigate(['my-sked'], {relativeTo: this.route, state: {fromInitialSelection: true}});
  }

  onChallengeClick(challenge: Partial<Project>) {
    this.router.navigate(['challenge', challenge._id], {relativeTo: this.route, state: {fromInitialSelection: true}});
  }

  onProjectClick(project: Partial<Project>) {
    this.router.navigate(['project', project._id], {relativeTo: this.route, state: {fromInitialSelection: true}});
  }

  // This is not triggered anywhere, will be used later
  onSalvageClick(): void {
    const fakeTasks: CsSalvageDialogTask[] = [
      {_id: '1', label: 'Go on a bike ride for 30 minutes', icon: 'bicycle'},
      {_id: '2', label: 'Drop and give me 100 push-ups', icon: 'gym'},
      {_id: '3', label: 'Paint an amazing work of art', icon: 'artistpalette'},
    ];

    this.dialog.open(CsSalvageDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      panelClass: 'cs-salvage-dialog',
      width: '100%',
      maxWidth: '438px',
      data: {tasks: fakeTasks},
    });
  }

  onNewProjectOrChallenge() {
    const dialogRef = this.dialog.open(ProjectsDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      ...PROJECTS_DIALOG_CONFIG,
      data: {},
    });

    dialogRef.afterClosed().subscribe(() => {
      this.store$.dispatch(new GetProjectsSummaryAction({}));
    });
  }
}
